import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface HabitDayData {
  date: string;
  totalHabits: number;
  completedHabits: number;
  completionRate: number;
  habits: {
    habitClientId: string;
    habitName: string;
    category: string;
    completed: boolean;
    value: number | null;
  }[];
}

export interface HabitTrendPoint {
  date: string;
  completionRate: number;
  totalCompleted: number;
  totalAssigned: number;
}

export interface CategoryCompliance {
  category: string;
  totalLogs: number;
  completedLogs: number;
  complianceRate: number;
  avgValue: number | null;
  habits: string[];
}

export interface HabitAnalytics {
  overallCompliance: number;
  currentStreak: number;
  longestStreak: number;
  totalCompletions: number;
  totalDays: number;
  byCategory: CategoryCompliance[];
  trends: HabitTrendPoint[];
  calendarData: HabitDayData[];
}

export function useHabitAnalytics(clientId: string | undefined, days: number = 30) {
  const [analytics, setAnalytics] = useState<HabitAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      const endDate = new Date().toISOString().split("T")[0];

      // Get all habit logs with habit details
      const { data: logs, error: logsError } = await supabase
        .from("habit_logs")
        .select(`
          id,
          habit_client_id,
          log_date,
          completed,
          value,
          habit_client:habit_clients (
            id,
            habit:habits (
              id,
              name,
              category,
              tracking_type,
              unit
            )
          )
        `)
        .eq("client_id", clientId)
        .gte("log_date", startDate)
        .lte("log_date", endDate)
        .order("log_date", { ascending: true });

      if (logsError) throw logsError;

      // Get active habit assignments count
      const { data: assignments } = await supabase
        .from("habit_clients")
        .select("id")
        .eq("client_id", clientId)
        .eq("is_active", true);

      const totalAssignedHabits = assignments?.length || 0;

      // Process calendar data - group by date
      const calendarMap = new Map<string, HabitDayData>();
      const categoryMap = new Map<string, CategoryCompliance>();

      for (const log of logs || []) {
        const date = log.log_date;
        const habitClient = log.habit_client as any;
        const habit = habitClient?.habit;
        const category = habit?.category || "custom";
        const habitName = habit?.name || "Unknown";

        // Calendar data
        if (!calendarMap.has(date)) {
          calendarMap.set(date, {
            date,
            totalHabits: 0,
            completedHabits: 0,
            completionRate: 0,
            habits: [],
          });
        }
        const dayData = calendarMap.get(date)!;
        dayData.totalHabits++;
        if (log.completed) dayData.completedHabits++;
        dayData.habits.push({
          habitClientId: log.habit_client_id,
          habitName,
          category,
          completed: log.completed || false,
          value: log.value,
        });

        // Category data
        if (!categoryMap.has(category)) {
          categoryMap.set(category, {
            category,
            totalLogs: 0,
            completedLogs: 0,
            complianceRate: 0,
            avgValue: null,
            habits: [],
          });
        }
        const catData = categoryMap.get(category)!;
        catData.totalLogs++;
        if (log.completed) catData.completedLogs++;
        if (!catData.habits.includes(habitName)) {
          catData.habits.push(habitName);
        }
      }

      // Calculate completion rates
      calendarMap.forEach((dayData) => {
        dayData.completionRate =
          dayData.totalHabits > 0
            ? Math.round((dayData.completedHabits / dayData.totalHabits) * 100)
            : 0;
      });

      categoryMap.forEach((catData) => {
        catData.complianceRate =
          catData.totalLogs > 0
            ? Math.round((catData.completedLogs / catData.totalLogs) * 100)
            : 0;
      });

      // Calculate trends (weekly aggregation)
      const weeklyTrends: HabitTrendPoint[] = [];
      const sortedDates = Array.from(calendarMap.keys()).sort();

      // Group by week
      let weekStart = sortedDates[0];
      let weekCompleted = 0;
      let weekTotal = 0;

      for (let i = 0; i < sortedDates.length; i++) {
        const date = sortedDates[i];
        const dayData = calendarMap.get(date)!;
        weekCompleted += dayData.completedHabits;
        weekTotal += dayData.totalHabits;

        // Every 7 days or at the end, push a trend point
        if ((i + 1) % 7 === 0 || i === sortedDates.length - 1) {
          weeklyTrends.push({
            date,
            completionRate: weekTotal > 0 ? Math.round((weekCompleted / weekTotal) * 100) : 0,
            totalCompleted: weekCompleted,
            totalAssigned: weekTotal,
          });
          weekCompleted = 0;
          weekTotal = 0;
        }
      }

      // Calculate overall metrics
      const totalLogs = logs?.length || 0;
      const completedLogs = logs?.filter((l) => l.completed).length || 0;
      const overallCompliance = totalLogs > 0 ? Math.round((completedLogs / totalLogs) * 100) : 0;

      // Calculate streak (consecutive days with any completion)
      let currentStreak = 0;
      let longestStreak = 0;
      let tempStreak = 0;
      const today = new Date().toISOString().split("T")[0];

      // Check backwards from today
      const sortedDatesDesc = [...sortedDates].reverse();
      let checkDate = new Date(today);

      for (let i = 0; i < days; i++) {
        const dateStr = checkDate.toISOString().split("T")[0];
        const dayData = calendarMap.get(dateStr);

        if (dayData && dayData.completedHabits > 0) {
          tempStreak++;
          if (i === 0 || currentStreak > 0) {
            currentStreak = tempStreak;
          }
        } else if (dayData) {
          // Had habits but didn't complete any - break streak
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 0;
          if (currentStreak === 0) break;
        }
        // No data for this day - skip (don't break streak)

        checkDate.setDate(checkDate.getDate() - 1);
      }
      longestStreak = Math.max(longestStreak, tempStreak);

      setAnalytics({
        overallCompliance,
        currentStreak,
        longestStreak,
        totalCompletions: completedLogs,
        totalDays: calendarMap.size,
        byCategory: Array.from(categoryMap.values()).sort((a, b) => b.complianceRate - a.complianceRate),
        trends: weeklyTrends,
        calendarData: Array.from(calendarMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
      });
    } catch (err) {
      console.error("Error fetching habit analytics:", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setLoading(false);
    }
  }, [clientId, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, loading, error, refetch: fetchAnalytics };
}

// Hook for daily trend data
export function useHabitTrends(clientId: string | undefined, days: number = 30) {
  const [data, setData] = useState<HabitTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    const fetchTrends = async () => {
      try {
        const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];

        const { data: logs, error } = await supabase
          .from("habit_logs")
          .select("log_date, completed")
          .eq("client_id", clientId)
          .gte("log_date", startDate)
          .order("log_date");

        if (error) throw error;

        // Group by date
        const byDate = new Map<string, { completed: number; total: number }>();
        for (const log of logs || []) {
          if (!byDate.has(log.log_date)) {
            byDate.set(log.log_date, { completed: 0, total: 0 });
          }
          const d = byDate.get(log.log_date)!;
          d.total++;
          if (log.completed) d.completed++;
        }

        const trends: HabitTrendPoint[] = Array.from(byDate.entries())
          .map(([date, { completed, total }]) => ({
            date,
            completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
            totalCompleted: completed,
            totalAssigned: total,
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        setData(trends);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [clientId, days]);

  return { data, loading, error };
}
